import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import "./DropdownNav.css";
import { ApiUrl, BaseUrl } from "../../../Admin/API/ApiUrl";
import { Download, DownloadIcon, Loader } from "lucide-react";
// import logo from "../../../assets/images/ijacna-logo.png";
import { useQuery } from "@tanstack/react-query";

export default function DropdownNav({ menuOpen, setMenuOpen }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [openDropdown, setOpenDropdown] = useState(null);
  // const [downloadList, setDownloadList] = useState([]);

  const navRef = useRef(null);

  const toggleDropdown = (name) => {
    setOpenDropdown(openDropdown === name ? null : name);
  };

  const fetchDownloads = async () => {
    const response = await fetch(`${ApiUrl}/downloads`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || "Error fetching downloads");
    }

    return Array.isArray(data) ? data : [];
  };

  const { data: downloadList = [], isLoading, isError, error } = useQuery({
    queryKey: ["downloads"],
    queryFn: fetchDownloads,
    staleTime: 5 * 60 * 1000, // cache for 5 mins
    cacheTime: 10 * 60 * 1000, // keep in memory
    refetchOnWindowFocus: false, // optional
  });

  const handleDownload = async (id, file) => {
    try {
      const cleanPath = file.replace(/\\/g, "/");
      const fileName = cleanPath.split("/").pop();
      const encodedFileName = encodeURIComponent(fileName);

      const fileUrl = `${ApiUrl}/downloads/file/${encodedFileName}`;

      // console.log("⬇ Download URL:", fileUrl);

      // Count API
      await fetch(`${ApiUrl}/downloads/count/${id}`, {
        method: "POST",
      });

      //  Direct download
      const a = document.createElement("a");
      a.href = fileUrl;
      a.click();

    } catch (error) {
      console.error(" Download failed:", error);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setOpenDropdown(null); // close dropdown
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  if (isLoading) return null;

  return (
    <>
      <div
        className={`ijacna-overlay ${menuOpen ? "show" : ""}`}
        onClick={() => setMenuOpen(false)}
      ></div>

      <nav ref={navRef} className={`ijacna-navbar ${menuOpen ? "open" : ""}`}>

        <div className="drawer-header">
          <img
            src="/images/ijacna-logo.png"
            alt="IJACNA Logo"
            className="drawer-logo"
          />
          <h3>IJACNA</h3>
          <button className="drawer-close-btn" onClick={() => setMenuOpen(false)}>
            ✕
          </button>
        </div>

        <ul>
          {/* Home */}
          <li className={location.pathname === "/" ? "active" : ""}>
            <Link to="/">Home</Link>
          </li>

          {/* Aim & Scope */}
          <li className={location.pathname === "/AimAndScope" ? "active" : ""}>
            <Link to={"/AimAndScope"}>Aim & Scope</Link>
          </li>

          {/* Authors Guidelines */}
          <li
            className={`ijacna-dropdown ${openDropdown === "authors" ? "open" : ""
              } ${["/AuthorsGuideline", "/Topics", "/Fees"].includes(
                location.pathname
              )
                ? "active"
                : ""
              }`}
          // ref={dropdownRef}

          >
            <span className="dropdown-toggle-btn"
              onClick={() => toggleDropdown("authors")}

            >
              Authors Guidelines <i className="fas fa-caret-down"></i>
            </span>

            <ul className="ijacna-dropdown-menu">
              <li
                className={
                  location.pathname === "/AuthorsGuideline" ? "active" : ""
                }
              >
                <Link to={"/AuthorsGuideline"} onClick={() => setMenuOpen(false)}>Authors Guideline</Link>
              </li>

              <li className={location.pathname === "/Topics" ? "active" : ""}>
                <Link to={"/Topics"} onClick={() => setMenuOpen(false)}>Topics</Link>
              </li>

              <li className={location.pathname === "/Fees" ? "active" : ""}>
                <Link to={"/Fees"} onClick={() => setMenuOpen(false)}>Fees</Link>
              </li>
            </ul>
          </li>

          {downloadList.length > 0 && (
            <li
              className={`ijacna-dropdown ${openDropdown === "downloads" ? "open" : ""
                } ${["/Copyrights", "/ManuscriptTemplate"].includes(
                  location.pathname
                )
                  ? "active"
                  : ""
                }`}
              onClick={() => toggleDropdown("downloads")}
            // ref={dropdownRef}

            >
              <span className="dropdown-toggle-btn">
                Downloads <i className="fas fa-caret-down"></i>
              </span>
              <ul className="ijacna-dropdown-menu">
                {downloadList.map((download) => (
                  <li key={download.id} style={{ color: "white" }}>
                    {/* <Download size={19} /> */}
                    <span
                      style={{ cursor: "pointer", color: "white", display: 'flex', gap: "4px" }}
                      onClick={(e) => {
                        e.stopPropagation(); // prevent parent click
                        handleDownload(download.id, download.file_path);
                        setOpenDropdown(null); // close dropdown
                        setMenuOpen(false)
                      }}
                    >
                      <DownloadIcon size={14} />
                      {download.type_name}
                    </span>
                  </li>
                ))}
              </ul>
            </li>
          )}

          <li
            className={
              location.pathname === "/OnlineSubmission" ? "active" : ""
            }
          >
            <Link to={"/OnlineSubmission"} onClick={() => setMenuOpen(false)}>Online Submission</Link>
          </li>

          {/* Historical Repository */}
          <li className={location.pathname === "/Volumes" ? "active" : ""}>
            <Link to={"/Volumes"} onClick={() => setMenuOpen(false)}>Historical Repository</Link>
          </li>

          {/* Editorial Panel */}
          <li
            className={`ijacna-dropdown ${openDropdown === "editorial" ? "open" : ""
              } ${["/Reviewers", "/JoinAsReviewer"].includes(location.pathname)
                ? "active"
                : ""
              }`}
            onClick={() => toggleDropdown("editorial")}
          // ref={dropdownRef}

          >
            <span className="dropdown-toggle-btn">
              Editorial Panel <i className="fas fa-caret-down"></i>
            </span>

            <ul className="ijacna-dropdown-menu">
              <li className={location.pathname === "/Reviewers" ? "active" : ""}>
                <Link to={"/Reviewers"} onClick={() => setMenuOpen(false)}>Reviewer</Link>
              </li>

              <li
                className={
                  location.pathname === "/JoinAsReviewer" ? "active" : ""
                }
              >
                <Link to={"/JoinAsReviewer"} onClick={() => setMenuOpen(false)}> Join As Reviewer</Link>
              </li>
            </ul>
          </li>

          {/* Contact Us */}
          <li className={location.pathname === "/ContactUs" ? "active" : ""}>
            <Link to={"/ContactUs"} onClick={() => setMenuOpen(false)}>Contact Us</Link>
          </li>
        </ul>
      </nav>
    </>
  );
}
